import { FC, forwardRef } from 'react';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Slide,
  Box,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import reactStringReplace from 'react-string-replace';
import { useTranslation } from 'react-i18next';

import { ViewNavigationBar } from '@components';
import { useGetSpacing } from '@ui';
import { useCreateOrder, OrderDetailDialog } from '@core/features/orders';
import { useCurrentVenueSlug, useGetVenue } from '@customer/features/venues';

import { useBasketStore } from '../stores';
import { useBasketLimit } from '../hooks';
import { BasketPositionRow } from './BasketPositionRow';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction="up"
      easing={{
        enter: 'cubic-bezier(0.66, 0, 0.34, 1)',
        exit: 'linear',
      }}
      ref={ref}
      {...props}
    />
  );
});

/**
 * The customer's shopping basket. Opened via `useDialogs`:
 *   dialogs.open(BasketDialog);
 *
 * Lists all positions from useBasketStore with inline quantity edit and
 * delete. The "Auftrag erstellen" button calls useCreateOrder with the
 * current venue (from the URL). On success the basket is cleared and the
 * OrderDetailDialog is opened for the freshly created order.
 */
export const BasketDialog: FC<DialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const getSpacing = useGetSpacing();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const dialogs = useDialogs();

  const { positions, clear } = useBasketStore();
  const venueSlug = useCurrentVenueSlug();
  const { data: venue } = useGetVenue(venueSlug);
  const { maxOrderQuantity, isOverLimit } = useBasketLimit();

  const { mutateAsync: createOrder, isPending } = useCreateOrder();

  const isEmpty = positions.length === 0;

  const handleCreateOrder = async () => {
    if (!venueSlug || isEmpty) return;
    try {
      const order = await createOrder({
        venue: venueSlug,
        positions: positions.map((p) => ({
          artwork: p.artworkId,
          quantity: p.quantity,
        })),
      });
      clear();
      onClose();
      await dialogs.open(OrderDetailDialog, order.id);
    } catch (error) {
      console.error('Failed to create order from basket', error);
      // The backend rejects orders exceeding the venue's
      // max_order_quantity with a localized message on `positions`.
      const positionsError = isAxiosError(error)
        ? error.response?.data?.positions
        : undefined;
      const message =
        Array.isArray(positionsError) && typeof positionsError[0] === 'string'
          ? positionsError[0]
          : t('orderCreateFailed');
      toast.error(message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      fullWidth={true}
      maxWidth="sm"
      fullScreen={fullScreen}
      scroll="paper"
      slots={{ transition: Transition }}
    >
      <ViewNavigationBar
        onClose={() => onClose()}
        closeTitle={t('close')}
        title={t('basketTitle')}
      />

      <DialogContent
        sx={{
          padding: 0,
          minHeight: 200,
          backgroundColor: '#FFFFFF',
        }}
      >
        {isEmpty ? (
          <Box
            sx={{
              padding: 6,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 400 }}>
              {t('basketEmpty')}
            </Typography>
          </Box>
        ) : (
          <Stack direction="column">
            {positions.map((p) => (
              <BasketPositionRow
                key={p.artworkId}
                artworkId={p.artworkId}
                quantity={p.quantity}
              />
            ))}
          </Stack>
        )}
      </DialogContent>

      {!isEmpty && (
        <DialogActions
          sx={{
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: getSpacing('s'),
            paddingRight: getSpacing('xl'),
            paddingLeft: getSpacing('xl'),
            paddingTop: getSpacing('m'),
            paddingBottom: getSpacing('m'),
          }}
        >
          {maxOrderQuantity !== null && (
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 400,
                textAlign: 'center',
                color: isOverLimit ? 'error.main' : 'text.secondary',
              }}
            >
              {isOverLimit
                ? t('basketLimitReached', { limit: maxOrderQuantity })
                : t('basketMaxQuantityHint', { limit: maxOrderQuantity })}
            </Typography>
          )}

          {venue?.name && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
                {t('orderToBePlacedAt')}
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 400,
                  textTransform: 'uppercase',
                }}
              >
                {reactStringReplace(venue.name, 'Nivea Haus', (match, i) => (
                  <Box component="b" key={i} sx={{ fontWeight: 600 }}>
                    {match}
                  </Box>
                ))}
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleCreateOrder}
            disabled={isPending || !venueSlug || isOverLimit}
            loading={isPending}
          >
            {t('orderNow')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
